import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import {
    Alert,
    AlertHandle,
    autoinject,
    bind,
    Enums,
    FormStore,
    inject,
    Modal,
    ModalHandle,
    ServicesAlert,
    TasksStore,
    TicketForm,
    TicketsStore,
} from 'Mri.Web.Common';
import * as React from 'react';
import { BsPlusSquare } from '@react-icons/all-files/bs/BsPlusSquare';
import { Button } from 'reactstrap';
import { ContractsStore } from '../Contracts/ContractsStore';
import { FavoriteButton } from '../Core/FavoriteButton';
import { ZendeskLink } from '../Core/ZendeskLink';
import { ActiveDisplay } from './ActiveDisplay';
import { DisplayEventGroup, DisplayEventsList } from './DisplayEventsList';
import { DisplaysStore } from './DisplaysStore';
import {ContractModal} from "../Contracts/ContractModal";

export interface ITicketModalProps {
    readonly display: ActiveDisplay;
    readonly modalHandle: ModalHandle;
    readonly selectedEvents?: Mri.Web.Internal.Controllers.Displays.DisplayEventModel[];
}

@autoinject
@observer
export class TicketModal extends React.Component<ITicketModalProps> {
    public render() {
        return (
            <Modal
                title={this.title}
                modalHandle={this.props.modalHandle}
                onClosed={this.onClose}
                footerClass="d-none"
                size="xl"
                className="my-2"
                onOpened={this.loadAsync}
            >
                <span className="text-center">
                    <Alert alertHandle={this.alertHandle} />
                </span>

                <div className="row px-2">
                    <div className="col-12 col-md-5">
                        <div className="card">
                            <div className="card-header bg-btn-primary">
                                Ticket Form
                                <ServicesAlert
                                    className="p-2"
                                    type="message"
                                    onClick={this.openContractModal}
                                    contract={this.contract}
                                    display={this.props.display}
                                />
                            </div>
                            <div className="card-body">
                                {this.ticketForm && (
                                    <TicketForm
                                        onFormChange={this.populateTicket}
                                        onSubmit={this.submitAsync}
                                        ticketForm={this.ticketForm}
                                        alertHandle={this.alertHandle}
                                    />
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="col-12 col-md-7">
                        {!!this.displayEvents?.length && (
                            <div className="card">
                                <div className="card-header bg-btn-primary">Events</div>

                                <div className="card-body">
                                    <DisplayEventsList
                                        displayEvents={this.displayEvents}
                                        renderRow={this.renderRow}
                                        serialNumber={this.props.display.serialNumber}
                                    />
                                </div>
                            </div>
                        )}
                    </div>

                    <Modal
                        modalHandle={this.modalHandle}
                        title={`Created ${this.createdTicketedId}`}
                        footer={
                            <div>
                                <Button onClick={this.modalHandle.close}>Go Back</Button>

                                <ZendeskLink id={this.createdTicketedId!} className="btn btn-primary">
                                    View Ticket
                                </ZendeskLink>
                            </div>
                        }
                    >
                        Succesfully Created Ticket
                    </Modal>
                </div>

                {this.contract && (
                    <ContractModal
                        contract={this.contract}
                        display={this.props.display}
                        modalHandle={this.contractModalHandle}
                        viewState="readOnly"
                    />
                )}
            </Modal>
        );
    }

    @bind
    @action
    private onClose() {
        this.ticketForm = undefined;
    }

    @bind
    private renderRow(displayEvent: Mri.DisplayEvents.DisplayEvent, group: DisplayEventGroup, isHead: boolean) {
        if (displayEvent.downtimeIncidentType == undefined) return;

        return (
            <div className="d-inline-block">
                <div className="d-flex align-items-center h-100">
                    <input
                        type="checkbox"
                        checked={this.checkedDisplayEventTypes.includes(displayEvent.downtimeIncidentType)}
                        onChange={e => this.onChange(e, displayEvent, group, isHead)}
                    />
                </div>
            </div>
        );
    }

    @bind
    @action
    private onChange(
        event: React.FormEvent<HTMLInputElement>,
        displayEvent: Mri.DisplayEvents.DisplayEvent,
        group: DisplayEventGroup,
        isHead: boolean
    ) {
        if (!this.ticketForm) throw new Error('Ticket Form Not Defined');

        const target = event.target as HTMLInputElement;

        const ii = this.checkedDisplayEvents.indexOf(displayEvent);
        if (target.checked && ii == -1) this.checkedDisplayEvents.push(displayEvent);
        else if (!target.checked && ii != -1) this.checkedDisplayEvents.splice(ii, 1);

        if (displayEvent.downtimeIncidentType) {
            const jj = this.checkedDisplayEventTypes.indexOf(displayEvent.downtimeIncidentType);
            if (target.checked && jj == -1) this.checkedDisplayEventTypes.push(displayEvent.downtimeIncidentType);
            else if (!target.checked && jj != -1) this.checkedDisplayEventTypes.splice(jj, 1);
        }

        this.ticketForm.fields[this.ticketsStore.formField('Downtime Incident Types')].onChange(
            this.checkedDisplayEventTypes.map(v => Enums.toString(Enums.DowntimeIncidentType, v)).join(' ')
        );
        if (this.ticketForm.fields[this.ticketsStore.formField('Subject')].value == this.autoGeneratedSummary) {
            this.autoGeneratedSummary =
                this.props.display.serialNumber + ': ' + this.checkedDisplayEvents.map(de => de.details).join(', ');
            this.ticketForm.fields[this.ticketsStore.formField('Subject')].onChange(this.autoGeneratedSummary);
        }
    }

    @bind
    @action
    private async loadAsync() {
        let defaultFormId = null;
        this.checkedDisplayEvents = [];
        this.checkedDisplayEventTypes = [];
        this.autoGeneratedSummary = null;
        this.createdTicketedId = undefined;

        try {
            await this.displaysStore.loadCurrentDisplayEventsAsync(this.props.display.serialNumber);
            await this.ticketsStore.loadCreateMetaAsync();

            if (this.props.display.contract) await this.contractsStore.loadAsync(this.props.display.contract, true);

            if (!this.ticketsStore.createMeta) throw new Error('Create Meta Undefined');

            const defaultForm = this.ticketsStore.createMeta.forms.find(f => f.default);

            if (defaultForm) defaultFormId = defaultForm.id;

            const temp: any = {
                ticket_form_id: defaultFormId,
                internal_note: null,
                tags: null,
            };

            temp[this.ticketsStore.formField('Description')] = null;
            temp[this.ticketsStore.formField('Subject')] = null;
            temp[this.ticketsStore.formField('Display Serial Number')] = null;

            this.ticketForm = new FormStore<any>(temp);

            this.populateTicket();
        } catch (e: any) {
            this.alertHandle.setError('There was an error while loading the Ticket Form');
        }
    }

    @bind
    @action
    private populateTicket() {
        if (!this.ticketForm) throw new Error('Ticket Form Not Defined');

        if (this.ticketForm.fields[this.ticketsStore.formField('Display Serial Number')])
            this.ticketForm.fields[this.ticketsStore.formField('Display Serial Number')].onChange(
                this.props.display.serialNumber
            );

        this.ticketForm.add(this.ticketsStore.formField('Downtime Incident Types') as any, null);

        if (this.props.selectedEvents) {
            const selectedTypes = this.props.selectedEvents.map(d => d.downtimeIncidentType);
            this.checkedDisplayEventTypes = selectedTypes.map(s => s!!);
            this.checkedDisplayEvents = this.props.selectedEvents;
            this.autoGeneratedSummary =
                this.props.display.serialNumber + ': ' + this.checkedDisplayEvents.map(de => de.details).join(', ');
            this.ticketForm.fields[this.ticketsStore.formField('Subject')].onChange(this.autoGeneratedSummary);
            this.ticketForm.fields[this.ticketsStore.formField('Downtime Incident Types')].onChange(
                this.checkedDisplayEventTypes.map(v => Enums.toString(Enums.DowntimeIncidentType, v)).join(' ')
            );
        }
    }

    @bind
    @action
    private async submitAsync(fields: any) {
        const task = this.tasksStore.startSessionTask('Send Support Ticket');

        try {
            const zendeskTicketModel = await this.ticketsStore.createAsync(fields);

            if (zendeskTicketModel) {
                task.complete();
                this.createdTicketedId = zendeskTicketModel.id;
                await this.modalHandle.confirm();
                if (this.ticketForm) this.ticketForm = undefined;
                this.props.modalHandle.close();
            } else {
                task.error('Id not found');
            }
        } catch (e: any) {
            task.error(e);
        }
    }

    @computed
    private get displayEvents() {
        return this.displaysStore.currentEvents.get(this.props.display.serialNumber);
    }

    @computed
    private get title() {
        return (
            <div className="w-100 text-center">
                <FavoriteButton className="float-start" display={this.props.display} />

                <div className="fw-bold flex-items-center justify-content-center">
                    <BsPlusSquare /> Create Ticket For: {this.props.display.serialNumber}
                    <div className="d-inline-block">
                        <ServicesAlert onClick={this.openContractModal} className="p-2" contract={this.contract} display={this.props.display} />
                    </div>
                </div>
            </div>
        );
    }

    @computed
    private get contract() {
        if (this.props.display.contract) return this.contractsStore.contracts.get(this.props.display.contract);
    }

    @bind
    @action
    private openContractModal() {
        this.contractModalHandle.open();
    }

    private contractModalHandle = new ModalHandle();
    private autoGeneratedSummary: string | null = null;
    @observable
    private checkedDisplayEvents: Mri.DisplayEvents.DisplayEvent[] = [];
    @observable
    private checkedDisplayEventTypes: Mri.DisplayApi.DowntimeIncidentTypes[] = [];
    @observable
    private createdTicketedId?: string;
    @observable
    private ticketForm?: FormStore<any>;
    private alertHandle = new AlertHandle();
    private modalHandle = new ModalHandle();
    @inject
    private displaysStore!: DisplaysStore;
    @inject
    private ticketsStore!: TicketsStore;
    @inject
    private contractsStore!: ContractsStore;
    @inject
    private tasksStore!: TasksStore;
}
